export type ChoiceKey = 'A' | 'B' | 'C' | 'D';

export type Question = {
  id: string;
  prompt: string;
  choices: Record<ChoiceKey, string>;
};

export type TestPayload = {
  testId: string;
  created_at: string;
  questions: Question[];
};

export type GenerationStatus = 'pending' | 'generating' | 'completed' | 'failed';

export type ProgressState = {
  testId: string;
  status: GenerationStatus;
  progress: { current: number; total: number; message: string | null };
  error: string | null;
};

export type ReviewEntry = {
  questionId: string;
  prompt: string;
  choices: Record<ChoiceKey, string>;
  correct_choice: ChoiceKey;
  student_choice: ChoiceKey | null;
  recommended_steps: string | null;
  is_correct: boolean;
  verified: boolean;
  verification_notes: string | null;
};

export type ProgressPayload = ProgressState & {
  created_at: string;
  updated_at: string;
  completed_at: string | null;
};

export type CreateResponse = {
  testId: string;
  created_at: string;
  status: GenerationStatus;
  progress: { current: number; total: number; message: string | null };
};

export type SubmitResponse = {
  attemptId: string;
  created_at: string;
  score_percent: number;
  review: ReviewEntry[];
};

export type AttemptListEntry = {
  id: string;
  created_at: string;
  score_percent: number;
  student: string | null;
  test_id: string;
  requested_count: number | null;
  requested_difficulty: number | null;
  requested_topics: string[] | null;
  topic_hint: string | null;
};

export type AttemptDetail = {
  attempt: {
    id: string;
    test_id: string;
    created_at: string;
    score_percent: number;
    student: string | null;
    requested_count: number | null;
    requested_difficulty: number | null;
    requested_topics: string[] | null;
    topic_hint: string | null;
  };
  review: ReviewEntry[];
};

export type StatusMessage = { type: 'ok' | 'err' | 'info'; text: string };

export const MATH_TOPICS = [
  {
    id: 'number_arithmetic',
    label: 'Number & Arithmetic',
    description:
      'Whole-number operations (BODMAS, estimation), fractions, decimals, percentages, and ratios/rates including proportion, scaling, and speed-time-distance.',
  },
  {
    id: 'algebra_patterns',
    label: 'Algebra & Patterns',
    description:
      'Arithmetic/geometric sequences, missing terms, forming rules, simple equations from word problems, and reasoning with inequalities.',
  },
  {
    id: 'measurement_geometry',
    label: 'Measurement & Geometry',
    description:
      'Perimeter, area, volume of prisms, angle relationships, properties of shapes/solids, symmetry, and unit conversions (length, mass, capacity, time).',
  },
  {
    id: 'data_statistics_probability',
    label: 'Data, Statistics & Probability',
    description:
      'Interpreting tables/graphs, calculating mean/median/mode/range, and working with simple probabilities and likelihood reasoning.',
  },
  {
    id: 'word_problems_reasoning',
    label: 'Word Problems & Applied Reasoning',
    description:
      'Multi-step real-world contexts (money, travel, measurement) that mix concepts and require logical deduction.',
  },
] as const;

export type MathTopicId = (typeof MATH_TOPICS)[number]['id'];

export const RESULTS_PAGE_SIZE = 10;
